import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {
  getAudioSettings,
  pauseGameAudio,
  playGameSound,
  resumeGameAudio,
  setAudioSetting,
  subscribeAudioSettings,
} from '../audio/gameAudio';
import { designTokens } from '../theme/theme';

export function GameAudioControls({ docked = false, expanded = true }) {
  const [settings, setSettings] = useState(getAudioSettings);
  const [musicBlocked, setMusicBlocked] = useState(false);

  useEffect(() => subscribeAudioSettings(setSettings), []);
  useEffect(() => () => pauseGameAudio(), []);

  useEffect(() => {
    const unlockFromGesture = async (event) => {
      if (!settings.soundEffects && !settings.music) return;
      await resumeGameAudio();

      const target = event.target;
      if (settings.soundEffects && target instanceof Element &&
          target.closest('button,[role="button"]') &&
          !target.closest('[data-game-audio-control]')) {
        playGameSound('tap');
      }
    };
    document.addEventListener('pointerdown', unlockFromGesture, true);
    return () => document.removeEventListener('pointerdown', unlockFromGesture, true);
  }, [settings.music, settings.soundEffects]);

  useEffect(() => {
    // Browsers only allow sound after a user gesture. Try to start straight away (works when the
    // browser already trusts the site), and otherwise keep a prompt up and retry on real activation
    // events; pointerdown alone does not count on every browser.
    if (!settings.music) {
      setMusicBlocked(false);
      return undefined;
    }
    const events = ['click', 'touchend', 'keydown'];
    let active = true;
    const removeListeners = () => events.forEach(name => document.removeEventListener(name, unlock, true));
    async function unlock() {
      const running = await resumeGameAudio();
      if (!active) return;
      setMusicBlocked(!running);
      if (running) removeListeners();
    }
    unlock();
    events.forEach(name => document.addEventListener(name, unlock, true));
    return () => {
      active = false;
      removeListeners();
    };
  }, [settings.music]);

  const toggleSetting = async (key) => {
    const nextValue = !settings[key];
    if (nextValue) {
      const running = await resumeGameAudio();
      if (!running) {
        setMusicBlocked(key === 'music');
        return;
      }
    }
    setAudioSetting(key, nextValue);
  };

  return (
    <Box data-game-audio-control sx={{
      position: docked ? 'relative' : 'fixed',
      left: docked ? 'auto' : 'max(8px, env(safe-area-inset-left))',
      top: docked ? 'auto' : 'max(8px, env(safe-area-inset-top))',
      zIndex: docked ? 'auto' : 10001,
      display: expanded ? 'flex' : 'contents', gap: 0.2, p: docked ? 0 : 0.35,
      borderRadius: docked ? 0 : '12px',
      bgcolor: docked ? 'transparent' : 'rgba(13,17,23,0.9)',
      border: docked ? 0 : '1px solid rgba(255,255,255,0.16)',
      backdropFilter: docked ? 'none' : 'blur(10px)',
    }}>
      <Tooltip title={settings.music ? 'Turn background music off' : 'Turn background music on'}>
        <IconButton
          tabIndex={expanded ? 0 : -1}
          data-game-audio-control
          aria-label={settings.music ? 'Turn background music off' : 'Turn background music on'}
          aria-pressed={settings.music}
          onClick={() => toggleSetting('music')}
          size="small"
          sx={{ color: settings.music ? designTokens.colors.primary : designTokens.colors.textSecondary, minWidth: 36, minHeight: 36 }}
        >
          {settings.music ? <MusicNoteIcon fontSize="small" /> : <MusicOffIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={settings.soundEffects ? 'Turn sound effects off' : 'Turn sound effects on'}>
        <IconButton
          tabIndex={expanded ? 0 : -1}
          data-game-audio-control
          aria-label={settings.soundEffects ? 'Turn sound effects off' : 'Turn sound effects on'}
          aria-pressed={settings.soundEffects}
          onClick={() => toggleSetting('soundEffects')}
          size="small"
          sx={{ color: settings.soundEffects ? designTokens.colors.success : designTokens.colors.textSecondary, minWidth: 36, minHeight: 36 }}
        >
          {settings.soundEffects ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      {musicBlocked && settings.music && (
        <Box role="status" sx={{
          position: 'fixed', right: 'max(12px, env(safe-area-inset-right))', visibility: 'visible', pointerEvents: 'none',
          bottom: 'calc(max(12px, env(safe-area-inset-bottom)) + 56px)', whiteSpace: 'nowrap',
          p: 0.75, px: 1.1, borderRadius: 1, color: 'var(--app-text-primary)', bgcolor: 'var(--app-surface)',
          border: '1px solid var(--app-border-medium)', fontSize: '0.72rem', fontWeight: 700,
        }}>
          🎵 Tap anywhere to start the music
        </Box>
      )}
    </Box>
  );
}
